/**
 * Platform-admin client — Sprint BJ.
 *
 * Three endpoints, all gated by admin.tenants scope on the server.
 * The admin app routes through the same JWT the rest of the API
 * uses; access is determined entirely by the scope claim.
 */

import { api, type RequestOptions } from "./client";


export interface PlatformTenantRow {
  id:                 string;
  slug:               string;
  name:               string;
  status:             "active" | "suspended" | string;
  created_at:         string;
  user_count:         number;
  current_month_usd:  number;
  monthly_hard_usd:   number | null;
}


export interface PlatformUsageRollup {
  tenant_count:      number;
  active_count:      number;
  total_users:       number;
  current_month_usd: number;
  period_start:      string;
  period_end:        string;
}


/** Sprint H2 — single-tenant cross-system health rollup. */
export interface TenantHealthDto {
  tenant_id:                string;
  tenant_slug:              string;
  status:                   string;
  locale:                   string;
  last_login_at:            string | null;
  last_conversation_at:     string | null;
  user_count:               number;
  current_month_usd:        number;
  projected_month_usd:      number;
  monthly_hard_usd:         number | null;
  projected_over_hard:      boolean;
  connectors_installed:     number;
  connectors_healthy:       number;
  is_healthy:               boolean;
}


export const platformApi = {
  listTenants(opts?: RequestOptions): Promise<PlatformTenantRow[]> {
    return api.get<PlatformTenantRow[]>("/platform/tenants", opts);
  },

  updateTenantStatus(
    id: string, status: "active" | "suspended", opts?: RequestOptions,
  ): Promise<PlatformTenantRow> {
    return api.patch<PlatformTenantRow>(
      `/platform/tenants/${id}/status`, { status }, opts,
    );
  },

  usageRollup(opts?: RequestOptions): Promise<PlatformUsageRollup> {
    return api.get<PlatformUsageRollup>("/platform/usage", opts);
  },

  /** Sprint H2 — per-tenant rollup that combines activity,
   *  spend forecast, and connector health into one card. */
  tenantHealth(id: string, opts?: RequestOptions): Promise<TenantHealthDto> {
    return api.get<TenantHealthDto>(`/platform/tenants/${id}/health`, opts);
  },
};
